// Modern A4 Sale Invoice Printing
export const printSale = (sale) => {
  const printWindow = window.open("", "_blank");

  const svgToDataUri = (svg) =>
    `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

  const brandaranLogo = svgToDataUri(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 220 220">
      <rect width="220" height="220" fill="white"/>
      <circle cx="110" cy="110" r="100" fill="none" stroke="#1e73be" stroke-width="4"/>
      <circle cx="110" cy="110" r="82" fill="none" stroke="#1e73be" stroke-width="3"/>
      <ellipse cx="110" cy="110" rx="62" ry="80" fill="none" stroke="#1e73be" stroke-width="3"/>
      <ellipse cx="110" cy="110" rx="30" ry="80" fill="none" stroke="#1e73be" stroke-width="3"/>
      <path d="M28 72h164M18 110h184M28 148h164" stroke="#1e73be" stroke-width="12" fill="none" stroke-linecap="round"/>
      <path d="M78 35c10 16 18 30 32 30s22-14 32-30" fill="none" stroke="#1e73be" stroke-width="12" stroke-linecap="round"/>
      <path d="M78 185c10-16 18-30 32-30s22 14 32 30" fill="none" stroke="#1e73be" stroke-width="12" stroke-linecap="round"/>
      <text x="110" y="34" text-anchor="middle" font-size="17" font-weight="700" fill="#244a87" font-family="Georgia, serif">BRADARAN QADARI</text>
      <text x="110" y="132" text-anchor="middle" font-size="58" font-style="italic" fill="#0d83d4" font-family="Georgia, serif">BQ</text>
      <text x="110" y="205" text-anchor="middle" font-size="20" fill="#244a87" font-family="Georgia, serif">*</text>
      <text x="20" y="111" text-anchor="middle" font-size="20" fill="#244a87" font-family="Georgia, serif">*</text>
      <text x="200" y="111" text-anchor="middle" font-size="20" fill="#244a87" font-family="Georgia, serif">*</text>
    </svg>
  `);

  const marsLogo = svgToDataUri(`
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 150">
      <rect width="360" height="150" fill="white"/>
      <text x="18" y="58" fill="#c11d24" font-size="74" font-family="Georgia, serif">Mars</text>
      <text x="18" y="105" fill="#666666" font-size="52" font-family="Georgia, serif">Remedies</text>
      <text x="118" y="135" fill="#666666" font-size="34" font-family="Georgia, serif">Pvt. Ltd.</text>
      <path d="M255 18c34 5 63 31 70 63c4 17 0 36-10 52" fill="none" stroke="#d74d57" stroke-width="3"/>
      <circle cx="278" cy="23" r="9" fill="#2c7be5"/>
      <circle cx="304" cy="28" r="8" fill="#e43f5a"/>
      <circle cx="322" cy="58" r="12" fill="#2c7be5"/>
      <circle cx="255" cy="86" r="14" fill="#2c7be5"/>
      <circle cx="286" cy="98" r="10" fill="#e43f5a"/>
    </svg>
  `);

  const currencySymbol =
    sale.currency_details?.symbol || sale.currency_details?.title || "AF";

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const customerName = sale.customer_details?.name || "Walk-in Customer";
  const employeeName = sale.employee_details?.name || "-";

  const extractAmount = (value) => {
    if (value == null || value === "") return 0;
    if (typeof value === "number") return value;
    if (typeof value === "object") {
      const firstValue = Object.values(value)[0];
      if (firstValue && typeof firstValue === "object") {
        return extractAmount(
          firstValue.source ?? firstValue.parsedValue ?? firstValue.amount
        );
      }
      return extractAmount(firstValue);
    }
    const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const formatMoney = (value) => extractAmount(value).toFixed(2);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const subtotal = (sale.sale_items || []).reduce(
    (sum, item) =>
      sum + extractAmount(item.quantity) * extractAmount(item.sale_price),
    0
  );
  const discountAmount = extractAmount(sale.discount);
  const totalAmount = Math.max(0, subtotal - discountAmount);
  const paidAmount =
    sale.received_amount != null && sale.received_amount !== ""
      ? extractAmount(sale.received_amount)
      : extractAmount(sale.total_paid);
  const balanceDue = Math.max(0, totalAmount - paidAmount);

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Sale Invoice</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { 
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          font-size: 12px;
          line-height: 1.4;
          color: #333;
          background: white;
        }
        .invoice {
          max-width: 210mm;
          margin: 0 auto;
          padding: 12mm;
          background: white;
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 2px solid #2196F3;
          gap: 12px;
        }
        .company-info {
          flex: 1;
        }
        .company-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 6px;
        }
        .company-logos {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }
        .company-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid #d8e6f6;
          border-radius: 8px;
          background: #fff;
          padding: 4px 6px;
        }
        .company-logo img {
          display: block;
          object-fit: contain;
        }
        .company-logo.brandaran img {
          width: 62px;
          height: 62px;
        }
        .company-logo.mars img {
          width: 124px;
          height: 52px;
        }
        .company-name {
          font-size: 18px;
          font-weight: bold;
          color: #2196F3;
          margin-bottom: 2px;
        }
        .company-contact {
          color: #666;
          font-size: 10px;
        }
        .invoice-info {
          text-align: right;
          flex: 1;
        }
        .invoice-title {
          font-size: 20px;
          font-weight: bold;
          color: #2196F3;
          margin-bottom: 2px;
        }
        .invoice-number {
          font-size: 12px;
          color: #666;
        }
        .details-section {
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
        }
        .detail-box {
          background: #f8f9fa;
          padding: 10px 12px;
          border-radius: 4px;
          flex: 1;
          margin: 0 4px;
        }
        .detail-box:first-child { margin-left: 0; }
        .detail-box:last-child { margin-right: 0; }
        .detail-title {
          font-weight: bold;
          color: #2196F3;
          margin-bottom: 2px;
          font-size: 11px;
        }
        .detail-content {
          color: #666;
          font-size: 10px;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 12px;
          background: white;
          border-radius: 4px;
          overflow: hidden;
          box-shadow: 0 1px 4px rgba(0,0,0,0.08);
          font-size: 12px;
        }
        .items-table th {
          background: #2196F3;
          color: white;
          padding: 10px 8px;
          text-align: left;
          font-weight: 600;
          font-size: 12px;
        }
        .items-table td {
          padding: 10px 8px;
          border-bottom: 1px solid #eee;
          line-height: 1.5;
        }
        .items-table tr:hover {
          background: #f8f9fa;
        }
        .items-table tr:last-child td {
          border-bottom: none;
        }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .summary-section {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 12px;
        }
        .summary-box {
          background: #f8f9fa;
          padding: 12px 14px;
          border-radius: 4px;
          min-width: 220px;
          font-size: 12px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
          padding: 3px 0;
        }
        .summary-row.total {
          border-top: 2px solid #2196F3;
          padding-top: 8px;
          margin-top: 8px;
          font-weight: bold;
          font-size: 14px;
          color: #2196F3;
        }
        .footer {
          text-align: center;
          margin-top: 14px;
          padding-top: 10px;
          border-top: 1px solid #eee;
          color: #666;
          font-size: 11px;
        }
        .thank-you {
          font-size: 12px;
          font-weight: bold;
          color: #2196F3;
          margin-bottom: 2px;
        }
        .note-section {
          margin-top: 12px;
          margin-bottom: 12px;
          padding: 10px 12px;
          background: #f8f9fa;
          border-radius: 4px;
          border-left: 3px solid #2196F3;
        }
        .note-title {
          font-weight: bold;
          color: #2196F3;
          font-size: 12px;
          margin-bottom: 4px;
        }
        .note-content {
          font-size: 11px;
          color: #333;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-word;
        }
        @media print {
          body { margin: 0; padding: 0; font-size: 11px; }
          .invoice { 
            box-shadow: none; 
            max-width: none;
            padding: 15mm;
          }
          .header { margin-bottom: 10px; padding-bottom: 8px; }
          .details-section { margin-bottom: 10px; }
          .detail-box { padding: 8px 10px; }
          .items-table { margin-bottom: 10px; }
          .items-table th, .items-table td { padding: 10px 8px; line-height: 1.5; font-size: 12px; }
          .summary-section { margin-bottom: 10px; }
          .summary-box { padding: 10px 12px; }
          .note-section { margin-top: 10px; margin-bottom: 10px; padding: 8px 10px; }
          .footer { margin-top: 12px; padding-top: 8px; }
          @page { 
            size: A4; 
            margin: 18mm;
          }
        }
      </style>
    </head>
    <body>
      <div class="invoice">
        <!-- Header -->
        <div class="header">
          <div class="company-info">
            <div class="company-brand">
              <div class="company-logos">
                <div class="company-logo brandaran">
                  <img src="${brandaranLogo}" alt="Sedis Niazi Wholesale Logo" />
                </div>
                <div class="company-logo mars">
                  <img src="${marsLogo}" alt="Mars Remedies Pvt. Ltd. Logo" />
                </div>
              </div>
            </div>
            <div class="company-name">Sedis Niazi Wholesale</div>
            <div class="company-contact">
              📞 +93 70 285 0840 - 0744465798<br>
              🏢 Hotel Parwan, Rahman Market, Shop # G22
            </div>
          </div>
          <div class="invoice-info">
            <div class="invoice-title">INVOICE</div>
            <div class="invoice-number">#${sale.id}</div>
          </div>
        </div>

        <!-- Details Section -->
        <div class="details-section">
          <div class="detail-box">
            <div class="detail-title">Bill To:</div>
            <div class="detail-content">
              <strong>${escapeHtml(customerName)}</strong>
              ${
                sale.customer_details?.contact &&
                sale.customer_details.contact.trim()
                  ? `<br>Phone: ${escapeHtml(sale.customer_details.contact)}`
                  : ""
              }
              ${
                sale.customer_details?.address &&
                sale.customer_details.address.trim()
                  ? `<br>Address: ${escapeHtml(sale.customer_details.address)}`
                  : ""
              }
              ${
                sale.customer_details?.email &&
                sale.customer_details.email.trim()
                  ? `<br>Email: ${escapeHtml(sale.customer_details.email)}`
                  : ""
              }
            </div>
          </div>
          <div class="detail-box">
            <div class="detail-title">Invoice Details:</div>
            <div class="detail-content">
              <strong>Visitor:</strong> ${escapeHtml(employeeName)}<br>
              <strong>Date:</strong> ${formatDate(sale.sale_date)}<br>
              <strong>Time:</strong> ${new Date().toLocaleTimeString()}<br>
              <strong>Currency:</strong> ${currencySymbol}<br>
              <strong>Items:</strong> ${sale.sale_items?.length || 0}
            </div>
          </div>
        </div>

        <!-- Items Table -->
        <table class="items-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Product</th>
              <th class="text-center">Qty</th>
              <th class="text-right">Unit Price</th>
              <th class="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${
              sale.sale_items && sale.sale_items.length > 0
                ? sale.sale_items
                    .map((item, index) => {
                      const name =
                        item.product_details?.name || `Product ${index + 1}`;
                      const qty = item.quantity;
                      const price = formatMoney(item.sale_price);
                      const total = formatMoney(
                        extractAmount(item.quantity) * extractAmount(item.sale_price)
                      );
                      return `
                  <tr>
                    <td>${index + 1}</td>
                    <td><strong>${name}</strong></td>
                    <td class="text-center">${qty}</td>
                    <td class="text-right">${price} ${currencySymbol}</td>
                    <td class="text-right"><strong>${total} ${currencySymbol}</strong></td>
                  </tr>
                `;
                    })
                    .join("")
                : '<tr><td colspan="5" class="text-center">No items found</td></tr>'
            }
          </tbody>
        </table>

        <!-- Summary Section -->
        <div class="summary-section">
          <div class="summary-box">
            <div class="summary-row">
              <span>Subtotal:</span>
              <span>${formatMoney(subtotal)} ${currencySymbol}</span>
            </div>
            <div class="summary-row">
              <span>Discount Amount:</span>
              <span>-${formatMoney(discountAmount)} ${currencySymbol}</span>
            </div>
            <div class="summary-row">
              <span>Total Amount:</span>
              <span>${formatMoney(totalAmount)} ${currencySymbol}</span>
            </div>
            <div class="summary-row">
              <span>Amount Paid:</span>
              <span>${formatMoney(paidAmount)} ${currencySymbol}</span>
            </div>
            <div class="summary-row total">
              <span>Balance Due:</span>
              <span>${formatMoney(balanceDue)} ${currencySymbol}</span>
            </div>
          </div>
        </div>

        ${
          sale.notes && String(sale.notes).trim()
            ? `
        <!-- Note -->
        <div class="note-section">
          <div class="note-title">Note</div>
          <div class="note-content">${String(sale.notes)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\n/g, "<br>")}</div>
        </div>
        `
            : ""
        }

        <!-- Footer -->
        <div class="footer">
          <div class="thank-you">Thank you for your business!</div>
          <div>© ${new Date().getFullYear()} Sedis Niazi Wholesale - All rights reserved</div>
          <div style="margin-top:4px;font-size:10px;color:#999;">Powered by Afghan Cosmos IT Solutions | www.afghancosmos.com</div>
        </div>
      </div>
      
      <script>
        window.onload = function() {
          setTimeout(() => {
            window.print();
            window.close();
          }, 1000);
        };
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
};

// Thermal receipt function
export const printThermalReceipt = (sale) => {
  const printWindow = window.open("", "_blank");

  const currencySymbol =
    sale.currency_details?.symbol || sale.currency_details?.title || "AF";

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const customerName = sale.customer_details?.name || "Cash";
  const employeeName = sale.employee_details?.name || "-";

  const extractAmount = (value) => {
    if (value == null || value === "") return 0;
    if (typeof value === "number") return value;
    if (typeof value === "object") {
      const firstValue = Object.values(value)[0];
      if (firstValue && typeof firstValue === "object") {
        return extractAmount(
          firstValue.source ?? firstValue.parsedValue ?? firstValue.amount
        );
      }
      return extractAmount(firstValue);
    }
    const parsed = parseFloat(String(value).replace(/[^0-9.-]/g, ""));
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const formatMoney = (value) => extractAmount(value).toFixed(2);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const subtotal = (sale.sale_items || []).reduce(
    (sum, item) =>
      sum + extractAmount(item.quantity) * extractAmount(item.sale_price),
    0
  );
  const discountAmount = extractAmount(sale.discount);
  const totalAmount = Math.max(0, subtotal - discountAmount);
  const paidAmount =
    sale.received_amount != null && sale.received_amount !== ""
      ? extractAmount(sale.received_amount)
      : extractAmount(sale.total_paid);
  const balanceDue = Math.max(0, totalAmount - paidAmount);

  const thermalContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Thermal Receipt</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: 'Courier New', monospace;
          font-size: 11px;
          line-height: 1.2;
          width: 80mm;
          margin: 0;
          padding: 0;
          background: white;
        }
        .thermal-wrapper {
          width: 100%;
          text-align: center;
          padding: 5px;
        }
        .company { font-size: 14px; font-weight: bold; margin-bottom: 2px; }
        .contact { font-size: 10px; margin-bottom: 1px; }
        .title { font-size: 12px; font-weight: bold; margin: 5px 0; }
        .line { display: flex; justify-content: space-between; margin: 1px 0; font-size: 10px; }
        .dash { border-top: 1px dashed #000; margin: 3px 0; }
        .item { margin: 2px 0; font-size: 10px; }
        .center { text-align: center; }
        @media print {
          body { width: 80mm; margin: 0; padding: 0; }
          @page { size: 80mm auto; margin: 0; }
        }
      </style>
    </head>
    <body>
      <div class="thermal-wrapper">
        <div class="company">Sedis Niazi Wholesale</div>
        <div class="contact">📞 +93 70 285 0840 - 0744465798</div>
        <div class="contact">🏢 Hotel Parwan, Rahman Market, Shop # G22</div>
        
        <div class="title">═══ SALE RECEIPT ═══</div>
        
        <div class="line">
          <span>Invoice:</span>
          <span>#${sale.id}</span>
        </div>
        <div class="line">
          <span>Date:</span>
          <span>${formatDate(sale.sale_date)}</span>
        </div>
        <div class="line">
          <span>Customer:</span>
          <span>${escapeHtml(customerName)}</span>
        </div>
        <div class="line">
          <span>Visitor:</span>
          <span>${escapeHtml(employeeName)}</span>
        </div>
        ${
          sale.customer_details?.contact && sale.customer_details.contact.trim()
            ? `
        <div class="line">
          <span>Phone:</span>
          <span>${escapeHtml(sale.customer_details.contact)}</span>
        </div>
        `
            : ""
        }
        ${
          sale.customer_details?.address && sale.customer_details.address.trim()
            ? `
        <div class="line">
          <span>Address:</span>
          <span>${escapeHtml(sale.customer_details.address.substring(0, 35))}</span>
        </div>
        `
            : ""
        }
        
        <div class="dash"></div>
        <div class="center" style="font-weight: bold;">ITEMS</div>
        <div class="dash"></div>
        
        ${
          sale.sale_items && sale.sale_items.length > 0
            ? sale.sale_items
                .map((item, index) => {
                  const name = (
                    item.product_details?.name || `Product ${index + 1}`
                  ).substring(0, 18);
                  const qty = item.quantity;
                  const total = formatMoney(
                    extractAmount(item.quantity) * extractAmount(item.sale_price)
                  );
                  return `
              <div class="item">
                <div style="font-weight: bold;">${name}</div>
                <div class="line">
                  <span>Qty: ${qty}</span>
                  <span>${total} ${currencySymbol}</span>
                </div>
              </div>
            `;
                })
                .join("")
            : '<div class="center">No items</div>'
        }
        
        <div class="dash"></div>
        <div class="line">
          <span>Subtotal:</span>
          <span>${formatMoney(subtotal)} ${currencySymbol}</span>
        </div>
        <div class="line">
          <span>Discount:</span>
          <span>-${formatMoney(discountAmount)} ${currencySymbol}</span>
        </div>
        <div class="line">
          <span>Total:</span>
          <span>${formatMoney(totalAmount)} ${currencySymbol}</span>
        </div>
        <div class="line">
          <span>Paid:</span>
          <span>${formatMoney(paidAmount)} ${currencySymbol}</span>
        </div>
        <div class="dash"></div>
        <div class="line" style="font-weight: bold;">
          <span>BALANCE:</span>
          <span>${formatMoney(balanceDue)} ${currencySymbol}</span>
        </div>
        <div class="dash"></div>
        
        <div class="center" style="margin-top: 10px; font-size: 10px;">
          Thank you!<br>
          ${new Date().getFullYear()} © Sedis Niazi Wholesale<br>
          <span style="font-size:9px;color:#999;">Powered by Afghan Cosmos IT Solutions | www.afghancosmos.com</span>
        </div>
      </div>

      <script>
        window.onload = function() {
          setTimeout(() => {
            window.print();
            window.close();
          }, 500);
        };
      </script>
    </body>
    </html>
  `;

  printWindow.document.write(thermalContent);
  printWindow.document.close();
};

const printSaleUtils = { printSale, printThermalReceipt };

export default printSaleUtils;
